import { Text } from 'react-native-paper';
import useFormStyle from '@/components/Styles/useFormStyle';
import { Redirect, useRouter } from 'expo-router';
import ScreenMargin from '@/components/ScreenMargin';
import { useForm } from 'react-hook-form';
import {
  InsertImplantMutationVariables,
  InsertPatientMutationVariables,
  useInsertImplantMutation,
  useListLaboratoiresQuery,
} from '@/data/data';
import { useUserContext } from '@/components/Auth/UserContext';
import { useState } from 'react';
import Form from '@/components/Form/Form';
import { View } from 'react-native';
import FTextInput from '@/components/Form/TextInput';
import BaseContainer from '@/components/BaseContainer';
import { AvailableValidationRules } from '@/components/Form/validation';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import { useApolloClient } from '@apollo/client';
import { getAdminLink, getNormalLink } from '@/components/Apollo/client';
import { useAuthContext } from '@/components/Auth/AuthContext';
import DropDownInput from '@/components/Form/DropDownInput';
import { useTranslation } from 'react-i18next';
import { defocusAbsisse } from '@/data/visionData';
import DefocusChart from '@/components/ScreenComponents/DefocusChart';

const AjouterImplantPage = () => {
  const { data: laboratoireList, loading: laboratoireListLoading } = useListLaboratoiresQuery();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { user } = useUserContext();
  const router = useRouter();
  const authUser = useAuthContext();
  const [loading, setLoading] = useState(false);

  const [insertImplant] = useInsertImplantMutation();
  const form = useForm<InsertImplantMutationVariables['object']>();

  const styles = useFormStyle();

  const defocusChartOrdonnees = form.watch('data') || [];
  const defocusChartData = defocusAbsisse.map((value, k) => [
    value,
    defocusChartOrdonnees[k] !== undefined ? defocusChartOrdonnees[k] : null,
  ]);

  if (authUser.payload && authUser.payload['cognito:groups'].indexOf('admin') <= -1) {
    return <Redirect href={'/patients'} />;
  }

  return (
    <ScreenMargin>
      <BaseContainer withScrollView={'keyboardAware'}>
        <Text style={styles?.headerForm}>{t('Ajouter un implant')}</Text>

        <Form<InsertImplantMutationVariables['object']> {...form}>
          <View style={styles?.bodyForm}>
            <View style={styles?.groupForm}>
              <Text style={styles?.titleGroupForm}>{t('Données')}</Text>
              <DropDownInput
                name={'laboratoireId'}
                label={t('Laboratoire')}
                searchPlaceholder={t('Chercher un laboratoire')}
                searchable={true}
                items={laboratoireList?.laboratoire || []}
                maxHeight={250}
                zIndex={4000}
                zIndexInverse={9000}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <FTextInput
                name={'name'}
                label={t('Nom')}
                withMargin={false}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <DropDownInput
                name={'materiau'}
                label={t('Matériau')}
                items={[
                  {
                    value: 'Hydrophobe',
                    label: t('Hydrophobe'),
                  },
                  {
                    value: 'Hydrophile',
                    label: t('Hydrophile'),
                  },
                ]}
                maxHeight={250}
                zIndex={5000}
                zIndexInverse={8000}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <DropDownInput
                name={'type'}
                label={t('Type')}
                items={[
                  {
                    value: 'EDOF',
                    label: 'EDOF',
                  },
                  {
                    value: 'MIOL',
                    label: 'MIOL',
                  },
                  {
                    value: 'Monofocal',
                    label: t('Monofocal'),
                  },
                  {
                    value: 'Addon',
                    label: t('Addon'),
                  },
                ]}
                maxHeight={250}
                zIndex={6000}
                zIndexInverse={7000}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <DropDownInput
                name={'mb'}
                label={t('Monoculaire ou Binoculaire')}
                items={[
                  {
                    value: 'Monocular',
                    label: t('Monoculaire'),
                  },
                  {
                    value: 'Binocular',
                    label: t('Binoculaire'),
                  },
                ]}
                maxHeight={250}
                zIndex={6000}
                zIndexInverse={7000}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <DropDownInput
                name={'correctionOptique'}
                label={t('CDVA / NDVA')}
                items={[
                  {
                    value: 'CDVA',
                    label: t('CDVA'),
                  },
                  {
                    value: 'NDVA',
                    label: t('NDVA'),
                  },
                ]}
                maxHeight={250}
                zIndex={6000}
                zIndexInverse={7000}
                containerStyle={styles.input2Col}
                validators={[AvailableValidationRules.required]}
              />
              <FTextInput
                name={'n'}
                label={t('Taille échantillon de test (N)')}
                withMargin={false}
                containerStyle={styles.input2Col}
              />
              <FTextInput
                name={'methodology'}
                label={t('Méthodologie')}
                withMargin={false}
                containerStyle={styles.input1Col}
              />
              <FTextInput
                name={'note'}
                label={t('Note')}
                withMargin={false}
                containerStyle={styles.input1Col}
              />
            </View>
            <View style={styles?.groupForm}>
              <Text style={styles?.titleGroupForm}>{t('Courbe de défocus')}</Text>
              <View style={styles?.input2Col}>
                {defocusAbsisse.map((value, k) => (
                  <FTextInput
                    name={`data.${k}`}
                    label={`${value} δ`}
                    key={k}
                    keyboardType={'decimal-pad'}
                    withMargin={false}
                  />
                ))}
              </View>
              <View style={styles?.input2Col}>
                <DefocusChart data={defocusChartData} />
              </View>
            </View>
          </View>
          <View style={styles?.footerForm}>
            <FormSectionFooter
              saveLoading={loading}
              saveDisabled={loading}
              onPress={form.handleSubmit(async (data) => {
                setLoading(true);
                if (user) {
                  client.setLink(getAdminLink());
                  const defocusChartOrdonnees = data.data;
                  const defocusChartData = defocusAbsisse.map((value, k) => [
                    value,
                    defocusChartOrdonnees[k] ? parseFloat(defocusChartOrdonnees[k]) : null,
                  ]);
                  await insertImplant({
                    variables: {
                      object: {
                        ...data,
                        display: data.name,
                        data: defocusChartData,
                      },
                    },
                    fetchPolicy: 'network-only',
                  });
                  client.setLink(getNormalLink());
                }
                setLoading(false);
                router.back();
              })}
            />
          </View>
        </Form>
      </BaseContainer>
    </ScreenMargin>
  );
};

export default AjouterImplantPage;
